import { useState } from 'react'
import { Link } from 'react-router'
import { backState } from '#app/components/back-link.tsx'
import { PageFrame } from '#app/components/page-frame.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { LISTED_DEMOS } from '#app/features/portfolio/demos/registry.ts'
import { getAllArticles } from '#app/features/writing/articles.ts'
import { formatDate } from '#app/features/writing/format-date.ts'
import { cn } from '#app/utils/misc.tsx'
import { type Route } from './+types/writing._index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Writing · slvr.mn' },
	{
		name: 'description',
		content:
			'Interactive demos, generator-first accounting explainers, and other writing.',
	},
]

// Raised from 10 when the demos joined this list: 8 demos permanently occupy the top of the
// page, so a 10-item page pushed all but two essays onto page 2. The list is meant to be
// scanned straight down. Drop this back when the combined count genuinely outgrows one page.
const PAGE_SIZE = 20

interface Entry {
	key: string
	title: string
	description: string
	href: string
	tags: readonly string[]
	/** ISO date for articles; demos carry a free-text span instead. */
	date?: string
	dateLabel: string
}

// Same two-bucket rule as the homepage: anything without a buy link is a Writing, so the
// demos list here too. Demos lead (their order encodes the AI-Engineer pivot), then the
// articles newest-first — identical ordering to the Writing section, so "See all" is a
// superset of what was on the homepage rather than a different list.
//
// A demo's filter tag is its `label` (Demo / Tool / Architecture), NOT its tech stack:
// article tags are topical, and folding in every technology would triple the chip row.
function allEntries(): Entry[] {
	const demos: Entry[] = LISTED_DEMOS.map((d) => ({
		key: d.href,
		title: d.title,
		description: d.blurb,
		href: d.href,
		tags: [d.label],
		dateLabel: d.dates,
	}))
	const articles: Entry[] = getAllArticles().map((a) => ({
		key: a.slug,
		title: a.title,
		description: a.description,
		href: `/writing/${a.slug}`,
		tags: a.tags ?? [],
		date: a.date,
		dateLabel: formatDate(a.date),
	}))
	return [...demos, ...articles]
}

export default function WritingIndex() {
	const entries = allEntries()
	const allTags = Array.from(new Set(entries.flatMap((e) => e.tags))).sort()
	const [active, setActive] = useState<string | null>(null)
	const [page, setPage] = useState(1)

	const filtered = active
		? entries.filter((e) => e.tags.includes(active))
		: entries
	const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
	// Clamp in case the filter shrank the list below the current page.
	const current = Math.min(page, pageCount)
	const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

	// Changing the tag filter resets to the first page.
	function selectTag(tag: string | null) {
		setActive(tag)
		setPage(1)
	}

	return (
		<PageFrame>
			<div className="w-full">
				<Link
					to="/#writing"
					className="text-muted-foreground hover:text-foreground group mb-8 inline-flex items-center gap-1 text-sm transition-colors"
				>
					<Icon name="arrow-left" className="size-4" />
					Home
				</Link>
				<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
					Writing
				</h1>

				{allTags.length > 0 && (
					<div className="mt-6 flex flex-wrap gap-2">
						<TagChip
							label="All"
							active={active === null}
							onClick={() => selectTag(null)}
						/>
						{allTags.map((tag) => (
							<TagChip
								key={tag}
								label={tag}
								active={active === tag}
								onClick={() => selectTag(tag)}
							/>
						))}
					</div>
				)}

				<div className="mt-8 flex flex-col gap-3">
					{shown.map((entry) => (
						<Link
							key={entry.key}
							to={entry.href}
							state={backState('/writing')}
							className="hover:bg-muted/50 group block rounded-lg border p-4 transition-colors"
						>
							<div className="flex items-center justify-between gap-2">
								<h2 className="text-sm font-semibold">{entry.title}</h2>
								{entry.date ? (
									<time
										dateTime={entry.date}
										className="text-muted-foreground shrink-0 text-xs"
									>
										{entry.dateLabel}
									</time>
								) : (
									<span className="text-muted-foreground shrink-0 text-xs">
										{entry.dateLabel}
									</span>
								)}
							</div>
							<p className="text-muted-foreground mt-1 text-sm text-pretty">
								{entry.description}
							</p>
							{entry.tags.length > 0 && (
								<div className="mt-2 flex flex-wrap gap-1">
									{entry.tags.map((tag) => (
										<span
											key={tag}
											className="text-muted-foreground rounded-full border px-2 py-0.5 text-[10px]"
										>
											{tag}
										</span>
									))}
								</div>
							)}
						</Link>
					))}
				</div>

				{pageCount > 1 && (
					<nav
						aria-label="Pagination"
						className="mt-8 flex items-center justify-between gap-2"
					>
						<button
							type="button"
							onClick={() => setPage(current - 1)}
							disabled={current <= 1}
							className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm transition-colors disabled:pointer-events-none disabled:opacity-40"
						>
							<Icon name="arrow-left" className="size-4" />
							Newer
						</button>
						<span className="text-muted-foreground text-xs">
							Page {current} of {pageCount}
						</span>
						<button
							type="button"
							onClick={() => setPage(current + 1)}
							disabled={current >= pageCount}
							className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm transition-colors disabled:pointer-events-none disabled:opacity-40"
						>
							Older
							<Icon name="arrow-left" className="size-4 rotate-180" />
						</button>
					</nav>
				)}
			</div>
		</PageFrame>
	)
}

function TagChip({
	label,
	active,
	onClick,
}: {
	label: string
	active: boolean
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={cn(
				'rounded-full border px-3 py-1 text-xs transition-colors',
				active
					? 'bg-foreground text-background border-foreground'
					: 'text-muted-foreground hover:bg-muted/50',
			)}
		>
			{label}
		</button>
	)
}
